import { Box, useTheme } from '@mui/material';
import React from 'react';
import { AxisOptions, Chart, Datum, DatumStyles } from 'react-charts';
import { CreditOutputAnalysis } from '../../models';

interface EstimatedDebtCapacityChartProps {
	remaining: number;
	refinanced: number;
	incremental: number;
	proforma: number;
	debtCapacity: number;
	header: CreditOutputAnalysis | null;
}

type Capacity = {
	metric: string;
	amount: number;
};

export const EstimatedDebtCapacityChart: React.FC<EstimatedDebtCapacityChartProps> = ({
	remaining,
	refinanced,
	incremental,
	proforma,
	debtCapacity,
	header,
}) => {
	const theme = useTheme();
	const capacity = header?.debtCapacity ?? debtCapacity;

	const data = [
		{
			label: 'Estimated Debt Capacity',
			data: [
				{
					metric: 'Remaining Debt',
					amount: remaining,
				},
				{
					metric: 'Refinanced Debt',
					amount: refinanced,
				},
				{
					metric: 'Incremental Debt',
					amount: incremental,
				},
				{
					metric: 'Proforma Debt',
					amount: proforma,
				},
				{
					metric: 'Estimated Debt Capacity',
					amount: capacity,
				},
			],
		},
	];

	const primaryAxis = React.useMemo(
		(): AxisOptions<Capacity> => ({
			getValue: data => data.metric,
		}),
		[]
	);

	const secondaryAxes = React.useMemo(
		(): AxisOptions<Capacity>[] => [
			{
				getValue: data => data.amount,
			},
		],
		[]
	);

	const getDatumStyle = React.useCallback(
		(datum: Datum<Capacity>): DatumStyles => ({
			fill:
				datum.originalDatum.metric == 'Estimated Debt Capacity'
					? theme.palette.primary.main
					: theme.palette.gray.medium,
		}),
		[theme]
	);

	return (
		<Box sx={{ minHeight: 300, marginBottom: 3 }}>
			<Chart
				options={{
					data,
					primaryAxis,
					secondaryAxes,
					getDatumStyle,
				}}
			/>
		</Box>
	);
};
